/* eslint-disable indent */
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { Tarifa, TiposEntradas, useCheckInMutation, useTarifasLazyQuery } from "src/gql/schema"
import { useProfile } from "src/shared/hooks/useProfile"
import { useDate } from "src/shared/hooks/useDate"
import useSnackbar from "src/shared/hooks/useSnackbar"
import { sum } from "src/shared/helpers/calculator"
import { getPaymentList } from "src/shared/sections/payment/Payment.helpers"
import { toggleRoomDetailsDrawer } from "src/store/navigation/navigationSlice"
import useValidarLovePoints from "../../hooks/useValidarLovePoints"
import { filtrarTarifasRenta } from "../../helpers/filtrarTarifasRenta"
import { VentaHabitacionCase } from "../VentaHabitacionCase.types"

// Check in de una reserva: se crea la renta a partir de la reserva con su tarifa
const useReservaCase = (): VentaHabitacionCase => {
    const location = useLocation()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { hotel_id, turno_hotel_id, usuario_id } = useProfile()
    const { diffDays, areSameDay, UTCStringToLocalDate } = useDate()
    const { showSnackbar } = useSnackbar()
    const [getTarifas] = useTarifasLazyQuery()
    const [checkIn] = useCheckInMutation()
    const { validarLovePoints } = useValidarLovePoints()

    const reserva = location?.state?.reservaSeleccionada

    return {
        reserva,
        cargarTarifas: (habitacion) =>
            getTarifas({
                variables: {
                    hotel_id,
                    tipo_habitacion_id: habitacion.habitacion.tipo_habitacion_id,
                },
            }).then((t) =>
                reserva?.tarifa?.tarifa_id
                    ? (t.data?.tarifas.filter((tt) => tt.tarifa_id === reserva.tarifa.tarifa_id) as Tarifa[])
                    : filtrarTarifasRenta(t.data)
            ),
        getEasyRewardsId: (habitacion) =>
            habitacion.habitacion?.ultima_reserva?.reserva?.pagos?.[0]?.detalles_pago?.[0]?.easyrewards_id || null,
        validarTarifaPorFecha: true,
        camposBloqueados: { tipo: false, hospedajesExtra: false, earlyCheckIn: false, personas: false, nombre: false },
        conservarExtrasDeReserva: false,
        // Si los pagos de la reserva no cubren la renta se muestra el pago completo, si la cubren solo abonar
        getSeccionPago: ({ isShowingPago, isShowingPagoReserva }) =>
            isShowingPago ? "pago" : isShowingPagoReserva ? "abonar" : null,
        onSubmit: async (data, ctx) => {
            const { habitacion, propinasFormatted, lovePointsAmount } = ctx
            const easyRewardsId = lovePointsAmount?.id

            const isLovePointsValid = await validarLovePoints(data, ctx)
            if (!isLovePointsValid) {
                return
            }

            const estadoPagoReserva = reserva?.estado_pago

            const pagoObject =
                estadoPagoReserva !== "pagado"
                    ? {
                        pago: {
                            detallesPago: [
                                ...getPaymentList(
                                    data.extra.map((pago) => ({
                                        ...pago,
                                        easyrewards_id: easyRewardsId,
                                    }))
                                ),
                            ],
                            total: sum([...getPaymentList(data.extra).map((v) => v.subtotal)]),
                            hotel_id,
                            usuario_id,
                        },
                    }
                    : {}
            await checkIn({
                variables: {
                    datos_checkIn: {
                        horas_extra_renta: data.extraHours,
                        early_checkin: data.earlyCheckIn,
                        ...(propinasFormatted.length
                            ? {
                                propina: {
                                    colaborador_id: data.colaboradorRecibioPropina?.id as string,
                                    detalles_pago: propinasFormatted,
                                    turno_id: turno_hotel_id,
                                },
                            }
                            : {}),
                        hospedajes_extra_renta: areSameDay(UTCStringToLocalDate(reserva?.fecha_salida), data.date[1])
                            ? 0
                            : diffDays(UTCStringToLocalDate(reserva?.fecha_salida), data.date[1]) - 1,
                        personas_extra_renta: data.extraUsers - reserva?.personas_extras,
                        ...(data.matricula
                            ? {
                                vehiculo: {
                                    modelo: data.modelo as string,
                                    matricula: data.matricula,
                                    color: data.color as string,
                                    marca: data.marca as string,
                                },
                            }
                            : {}),
                        fecha_salida: reserva.fecha_salida,
                        tipo_entrada: data.entryType as TiposEntradas,
                        usuario_id,
                        reserva_id: reserva.reserva_id,
                        ...(data?.extra?.length && estadoPagoReserva !== "pagado" ? pagoObject : {}),
                        easyrewards_id: easyRewardsId,
                    },
                },
            })
                .then((res) => {
                    showSnackbar({
                        text: `La habitación **${habitacion?.habitacion.tipo_habitacion?.nombre} ${habitacion?.habitacion.numero_habitacion}** ha sido vendida`,
                        status: "success",
                        title: "Renta de habitación exitosa",
                    })
                    dispatch(toggleRoomDetailsDrawer(false))
                    if (res?.data?.checkIn[0]) {
                        window.location.href = `print://${res?.data?.checkIn[0]}_0/`
                    }
                    navigate("/u")
                })
                .catch(() => {
                    showSnackbar({
                        title: "Error al rentar habitación",
                        status: "error",
                        text: "¡Ups! Se ha producido un error. Por favor, inténtalo nuevamente",
                    })
                })
                .finally(() => {
                    dispatch(toggleRoomDetailsDrawer(false))
                })
        },
    }
}

export default useReservaCase
